import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import { browserEnvironment, parseWorkerMessage, refreshPush } from "../../lib/push";
import { pushConfig } from "../../lib/runtime-config";
import { useToast } from "../../ui/toast";

/**
 * App-wide push plumbing: a push that arrives while KitWork+ is visible becomes an in-app toast
 * (the worker hands it over instead of showing a system notification), a notification click
 * while the app is open navigates here, and the FCM token is refreshed once per start.
 */
export function PushListener() {
  const toast = useToast();
  const router = useRouter();
  const { config, firebase } = useClients();
  const me = useMe();

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const container = navigator.serviceWorker;
    const onMessage = (event: MessageEvent) => {
      const message = parseWorkerMessage(event.data, window.location.origin);
      if (message === null) return;
      if (message.kind === "open") {
        router.history.push(message.path);
        return;
      }
      toast({
        message: message.body.length === 0 ? message.title : `${message.title}: ${message.body}`,
        action: { label: "Open", href: message.path },
      });
    };
    container.addEventListener("message", onMessage);
    container.startMessages();
    return () => container.removeEventListener("message", onMessage);
  }, [router, toast]);

  useEffect(() => {
    const push = pushConfig(config);
    if (push === null) return;
    const env = browserEnvironment(true);
    if (!env.serviceWorker || !env.notification) return;
    const platform = env.ios && env.standalone ? "ios-pwa" : "web";
    refreshPush(firebase, me.uid, push.vapidKey, platform).catch((error: unknown) => {
      console.warn("push token refresh failed", error);
    });
  }, [config, firebase, me.uid]);

  return null;
}
